import {
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
  useId,
  useRef,
  useState,
} from 'react';

import { useContactSuggestions } from '@/shared/contacts/useContacts';
import { type ContactSuggestion, peopleText } from '@/shared/contacts/types';
import { notAnAddress } from '@/shared/emails/sending';
import { UsersIcon, XIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';
import { useDebounced } from '@/shared/utils/useDebounced';

import {
  addressLabel,
  addSuggestion,
  commitText,
  endsInBracketedAddress,
  groupLine,
  holdsAddress,
  isSeveral,
  looksLikeAddress,
  type Recipient,
  type RecipientsValue,
} from './recipients';

/**
 * «Para», «Copia» or «Copia oculta» of the mail manager (contract v10.44, ADR 0038 of the
 * back): chips and a box to type in, with suggestions from the address book
 * (`GET /api/contacts/suggest`, the most written to from `mailbox` first).
 *
 * A combobox in the ARIA sense: the box is the `combobox`, the suggestions a `listbox`
 * whose active option the box names in `aria-activedescendant`, so the focus never leaves
 * the box. Arrows move through the suggestions, Enter takes the one marked or makes a chip
 * of what is typed. Nothing is marked by itself unless the list answers what is typed
 * now: while the next answer comes the previous one stays on screen, and Enter on it
 * would take a suggestion for something else. Nor while what is typed already holds a
 * whole address, «Ana Pérez <ana@x.es>» too: that is taken as written. Comma and semicolon
 * make a chip, and so does a space after a whole address (before one, a space is part of a
 * name being looked for). Backspace on an empty box takes the last chip off, Escape closes
 * the suggestions and not the sheet, and pasting several addresses makes one chip each, in
 * place of what was selected. Leaving the box keeps what was typed as a chip: nothing
 * typed is lost by clicking «Enviar». Keys that compose a character (a dead key, an
 * input method) are the browser's, not the field's.
 *
 * A group is a chip of its own, never its addresses: the form sends it in `group_ids` and
 * the back sends it as a blind copy to each member.
 */
export function RecipientField({
  label,
  value,
  onChange,
  mailbox,
  optional = false,
  hint,
  error,
  disabled = false,
  autoFocus = false,
}: {
  label: string;
  value: RecipientsValue;
  onChange: (next: RecipientsValue) => void;
  /** The mailbox it leaves from: the suggestions rank by what was written from it. */
  mailbox: string;
  optional?: boolean;
  hint?: ReactNode;
  error?: string | undefined;
  disabled?: boolean;
  /** The box the sheet focuses when it opens (`data-autofocus` of `Dialog`): without it the
   * first chip's «×» would take the focus, being the first button of the sheet. */
  autoFocus?: boolean;
}) {
  const id = useId();
  const inputId = `${id}-input`;
  const listId = `${id}-list`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const typed = value.text.trim();
  const debounced = useDebounced(typed, 200);
  const suggestions = useContactSuggestions(debounced, mailbox);

  const options = listedOptions(typed && open ? (suggestions.data ?? []) : [], value.chips);
  const listOpen = options.length > 0;
  // The list on screen is the answer to what is typed only once the 200 ms have passed and
  // the answer for it came; until then it is the previous one, kept so it does not blink.
  const answersTyped = debounced === typed && !suggestions.isPlaceholderData;
  // While what is typed is a name or part of one, Enter takes the first suggestion of that
  // name; a whole address is taken as written, even when the book knows another one.
  const marked =
    active >= 0 && active < options.length
      ? active
      : listOpen && answersTyped && !holdsAddress(typed)
        ? 0
        : -1;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  function change(next: RecipientsValue): void {
    onChange(next);
    setActive(-1);
  }

  function commit(): void {
    if (value.text.trim()) change(commitText(value));
  }

  function pick(option: ContactSuggestion): void {
    change(addSuggestion(value, option));
    setOpen(false);
    input.current?.focus();
  }

  function remove(index: number): void {
    change({ ...value, chips: value.chips.filter((_, at) => at !== index) });
    input.current?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    // Enter or a space that ends composing «ñ» or a word in an input method is not a chip.
    if (event.nativeEvent.isComposing) return;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!listOpen) {
          setOpen(true);
          return;
        }
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const from = marked < 0 ? (step > 0 ? -1 : 0) : marked;
        setActive((from + step + options.length) % options.length);
        return;
      }
      case 'Enter': {
        // A recipient field never sends the form: Enter is for making a chip.
        event.preventDefault();
        const option = marked >= 0 ? options[marked] : undefined;
        if (option) pick(option);
        else commit();
        return;
      }
      case ',':
      case ';':
        event.preventDefault();
        commit();
        return;
      case ' ':
        if (looksLikeAddress(value.text) || endsInBracketedAddress(value.text)) {
          event.preventDefault();
          commit();
        }
        return;
      case 'Backspace':
        if (value.text === '' && value.chips.length > 0) {
          event.preventDefault();
          remove(value.chips.length - 1);
        }
        return;
      case 'Escape':
        if (listOpen) {
          // The suggestions close; the sheet behind stays open.
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
          setActive(-1);
        }
        return;
      default:
        return;
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>): void {
    const pasted = event.clipboardData.getData('text');
    if (!isSeveral(pasted)) return;
    event.preventDefault();
    // In place of what was selected, as a paste does; a space keeps it from gluing to a
    // word on either side.
    const box = event.currentTarget;
    const start = box.selectionStart ?? value.text.length;
    const end = box.selectionEnd ?? start;
    const before = value.text.slice(0, start);
    const after = value.text.slice(end);
    const glue = (side: string, edge: RegExp) => (side && !edge.test(side) ? ' ' : '');
    change(
      commitText(
        value,
        `${before}${glue(before, /[\s,;]$/)}${pasted}${glue(after, /^[\s,;]/)}${after}`,
      ),
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-ink-2 text-[13.5px] font-medium">
        {label}
        {/* The space is for the accessible name: «Copia opcional», not «Copiaopcional». */}
        {optional ? (
          <>
            {' '}
            <span className="text-ink-4 ml-1 font-normal">opcional</span>
          </>
        ) : null}
      </label>
      <div className="relative">
        <div
          className={cn(
            'bg-card flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border px-2 py-1',
            'focus-within:border-accent focus-within:ring-accent/25 focus-within:ring-2',
            error ? 'border-danger' : 'border-line',
            disabled && 'bg-paper-2',
          )}
          onMouseDown={(event) => {
            // A click on the field, between the chips, writes in the box.
            if (event.target === event.currentTarget) {
              event.preventDefault();
              input.current?.focus();
            }
          }}
        >
          {value.chips.length > 0 ? (
            <ul aria-label={`${label}: destinatarios`} className="contents">
              {value.chips.map((chip, index) => (
                <Chip
                  key={chip.kind === 'group' ? `g-${chip.id}` : `a-${chip.address.toLowerCase()}`}
                  chip={chip}
                  disabled={disabled}
                  onRemove={() => remove(index)}
                />
              ))}
            </ul>
          ) : null}
          <input
            ref={input}
            id={inputId}
            role="combobox"
            aria-expanded={listOpen}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={marked >= 0 ? `${listId}-${marked}` : undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy || undefined}
            autoComplete="off"
            spellCheck={false}
            {...(autoFocus ? { 'data-autofocus': true } : {})}
            disabled={disabled}
            className="text-ink placeholder:text-ink-4 h-7 min-w-[10rem] flex-1 bg-transparent px-1 text-[14.5px] outline-none"
            value={value.text}
            onChange={(event) => {
              onChange({ ...value, text: event.target.value });
              setActive(-1);
              setOpen(true);
            }}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              setOpen(false);
              setActive(-1);
              commit();
            }}
          />
        </div>
        <ul
          id={listId}
          role="listbox"
          aria-label={`Sugerencias para ${label}`}
          hidden={!listOpen}
          className="border-line bg-card absolute inset-x-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-md border py-1 shadow-lg"
        >
          {options.map((option, index) => (
            <li
              key={`${option.kind}-${option.id}`}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === marked}
              // Picking does not take the focus away from the box (and its blur would
              // turn what was typed into a chip first).
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(option)}
              className={cn(
                'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[14px] [overflow-wrap:anywhere]',
                index === marked ? 'bg-paper-2 text-ink' : 'text-ink-2',
              )}
            >
              {option.kind === 'group' ? (
                <>
                  <UsersIcon size={14} className="text-ink-3 shrink-0" />
                  <span>
                    <span className="text-ink font-medium">{option.label}</span>
                    <span className="text-ink-3"> · {peopleText(option.members ?? 0)}</span>
                  </span>
                </>
              ) : (
                <span>{option.label}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
      {hint ? (
        <p id={hintId} className="text-ink-3 text-[12.5px] leading-snug">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-danger text-[13px]">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Groups first, then contacts; nothing the field already has. */
function listedOptions(
  suggestions: readonly ContactSuggestion[],
  chips: readonly Recipient[],
): ContactSuggestion[] {
  const fresh = suggestions.filter((option) =>
    option.kind === 'group'
      ? !chips.some((chip) => chip.kind === 'group' && chip.id === option.id)
      : !chips.some(
          (chip) =>
            chip.kind === 'address' &&
            chip.address.toLowerCase() === (option.address ?? '').toLowerCase(),
        ),
  );
  return [
    ...fresh.filter((option) => option.kind === 'group'),
    ...fresh.filter((option) => option.kind !== 'group'),
  ];
}

function Chip({
  chip,
  disabled,
  onRemove,
}: {
  chip: Recipient;
  disabled: boolean;
  onRemove: () => void;
}) {
  const isGroup = chip.kind === 'group';
  const text = isGroup ? chip.name : addressLabel(chip);
  const bad = !isGroup && notAnAddress([chip.address]) !== null;
  return (
    <li
      className={cn(
        'inline-flex max-w-full min-w-0 items-center gap-1 rounded-full py-0.5 pr-0.5 pl-2.5 text-[13px] leading-5',
        isGroup
          ? 'bg-accent-soft text-accent-2'
          : bad
            ? 'bg-danger-soft text-danger'
            : 'bg-paper-2 text-ink',
      )}
      title={
        isGroup
          ? `${groupLine(chip.name, chip.members)}, en copia oculta`
          : bad
            ? 'No es una dirección de correo'
            : chip.address
      }
    >
      {isGroup ? <UsersIcon size={13} className="shrink-0" /> : null}
      <span className="min-w-0 [overflow-wrap:anywhere]">
        {isGroup ? (
          <>
            {text}
            <span className="opacity-75"> · {chip.members}</span>
            <span className="sr-only"> (grupo, en copia oculta)</span>
          </>
        ) : (
          text
        )}
      </span>
      <button
        type="button"
        disabled={disabled}
        aria-label={isGroup ? `Quitar el grupo ${chip.name}` : `Quitar ${chip.address}`}
        onClick={onRemove}
        className="hover:bg-ink/10 focus-visible:outline-accent inline-flex size-5 shrink-0 items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2"
      >
        <XIcon size={12} />
      </button>
    </li>
  );
}
